import React, { useCallback } from 'react'
import { useMemo } from 'react'
import { useAddressesQuery } from '../../../../api/options/hooks'
import { useStartProtectionDays } from '../../../../api/timeTerms/hooks'
import { ActivationObject } from '../../../providers/ActivationObjectProvider/types'
import { DropdownItem } from '../../../../types'
import { ServiceFutureDescList } from '../constants'
import { Step5AresProps, Step5FormOptionsProps, Step5FormProps } from '../types'

const useStep5FormOptions = (
    form: Step5FormProps['form'],
    ares: Step5AresProps,
    activationObject?: ActivationObject
): Step5FormOptionsProps => {
    const { data: startProtectionDays, error: startProtectionDaysError } = useStartProtectionDays(activationObject?.synonym, true)

    const zipCode = form.watch('invoiceAddressZIP')
    const [zipCodeSearch, setZipCodeSearch] = React.useState<string | undefined>(zipCode)
    const handleZipCodeSearchChanged = useCallback((zip: string) => setZipCodeSearch(zip), [])
    React.useEffect(() => {
        setZipCodeSearch(zipCode)
    }, [zipCode])

    const {
        data: addresses,
        error: zipCodeSearchError,
        isLoading: zipCodeSearchLoading,
    } = useAddressesQuery(zipCodeSearch?.replace(' ', ''))

    const { data: citiesData, error: citiesError, isLoading: citiesLoading } = useAddressesQuery(zipCode)

    const zipCodes = useMemo(() => {
        if (addresses)
            return addresses.map((adr) => {
                return { value: adr.zip, label: adr.zip }
            })
        return []
    }, [addresses])

    const cities = useMemo(() => {
        if (zipCode) {
            const arr = citiesData && citiesData[0] ? citiesData[0].cities : []
            if (ares && ares.companyData && arr.findIndex((x) => x.name === ares.companyData?.city) === -1)
                arr.push({ name: ares.companyData.city })
            return arr
        }
        return undefined
    }, [ares, citiesData, zipCode])

    const error = useMemo(() => {
        if (startProtectionDaysError)
            return 'Chyba při dotazu na dostupné dny počátku ochrany: ' + startProtectionDaysError.detail
        if (zipCodeSearchError) return 'Chyba při dotazu na směrovací čísla: ' + zipCodeSearchError.detail
        if (citiesError) return 'Chyba při dotazu na města dle PSČ: ' + citiesError.detail
    }, [citiesError, startProtectionDaysError, zipCodeSearchError])

    const invoiceEmails: DropdownItem[] = useMemo(() => {
        return (
            activationObject?.personContacts
                ?.filter((contact) => contact.email !== undefined && contact.email !== null && contact.email !== '')
                .filter((v, i, a) => a.findIndex((x) => x.email === v.email) === i)
                .map((contact) => {
                    return {
                        value: contact.email || '',
                        label: `${contact.fullName} <${contact.email}>`,
                    }
                }) || []
        )
    }, [activationObject?.personContacts])
    return {
        services: ServiceFutureDescList,
        startProtectionDays,
        error,
        zipCodes,
        zipCodeSearch,
        handleZipCodeSearchChanged,
        cities,
        sendDocumentationEmails: invoiceEmails,
        isLoading: zipCodeSearchLoading || citiesLoading,
    }
}

export default useStep5FormOptions
